// File: src/components/admin/MarketplaceDialog.tsx
// FINAL CORRECTED VERSION. The form now only contains fields that actually exist.

import React, {useEffect, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Marketplace} from '../../types';
import {createMarketplace, updateMarketplace} from '../../services/api';

interface MarketplaceDialogProps {
    open: boolean;
    onClose: () => void;
    marketplace: Partial<Marketplace> | null;
}

const initialState = {name: '', code: ''};

const MarketplaceDialog: React.FC<MarketplaceDialogProps> = ({open, onClose, marketplace}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState<Partial<Marketplace>>(initialState);

    useEffect(() => {
        setFormData(marketplace || initialState);
    }, [marketplace, open]);

    const mutation = useMutation({
        mutationFn: (data: Partial<Marketplace>) => data.id ? updateMarketplace(data.id, data) : createMarketplace(data as any),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['marketplaces']});
            onClose();
        },
        onError: (e) => alert((e as Error).message),
    });

    const handleSubmit = () => mutation.mutate(formData);
    const handleChange = (field: keyof typeof initialState) => (e: React.ChangeEvent<HTMLInputElement>) => setFormData({
        ...formData,
        [field]: e.target.value
    });

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>
                {formData.id ? t('marketplace.edit_marketplace') : t('marketplace.add_marketplace')}
            </DialogTitle>
            <DialogContent dividers>
                <TextField
                    autoFocus
                    margin="dense"
                    label={t('marketplace.name')}
                    fullWidth
                    value={formData.name || ''}
                    onChange={handleChange('name')}
                />
                <TextField
                    margin="dense"
                    label={t('marketplace.code')}
                    fullWidth
                    value={formData.code || ''}
                    onChange={handleChange('code')}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={mutation.isLoading}>
                    {t('marketplace.cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={mutation.isLoading}
                >
                    {mutation.isLoading ? t('marketplace.saving') : t('marketplace.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MarketplaceDialog;
